import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState, useRef } from 'react'
import { handleCallback, getAuthError, clearAuthError } from '../services/auth'
import { useAuth } from '../hooks/useAuth'
import { logger } from '../utils/logger'

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallback,
})

function AuthCallback() {
  const navigate = useNavigate()
  const { oauthComplete } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const processingRef = useRef(false)
  const processedRef = useRef(false)

  useEffect(() => {
    // Prevent multiple OAuth processing using refs
    if (processingRef.current || processedRef.current) {
      return
    }
    processingRef.current = true;
    
    (async () => {
      try {
        const result = await handleCallback()
        
        // Mark as processed after successful callback
        processedRef.current = true
        
        // handleCallback stores user in localStorage, but we need immediate React state update
        // for SPA navigation to work without showing login wall
        oauthComplete(result.user)
        
        // Use TanStack Router to navigate to intended destination (from result.redirectTo)
        const redirectTo = result.redirectTo || '/courses'
        
        if (result.isNewUser && !result.user.intakeCompleted) {
          navigate({ to: '/onboarding/step-1', replace: true })
        } else {
          // Add newUser query param if this is a first-time signup
          navigate({ 
            to: redirectTo, 
            search: result.isNewUser ? { newUser: 'true' } : undefined,
            replace: true 
          })
        }
      } catch (e: unknown) {
        processingRef.current = false // Reset on error to allow retry
        
        logger.error('OAuth callback failed', { error: e instanceof Error ? e.message : String(e) })
        
        // Check for specific auth errors
        const authError = getAuthError()
        if (authError) {
          clearAuthError()
          
          // Store error message for login page to display
          localStorage.setItem('login:message', JSON.stringify({
            type: authError.type,
            message: authError.message,
            provider: authError.provider
          }))
          
          // Redirect to login page
          navigate({ to: '/signin', replace: true })
          return
        }
        
        setError((e as Error)?.message || 'Sign-in failed')
      }
    })()
  }, [navigate, oauthComplete])

  return (
    <div style={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
      <div style={{ maxWidth: 420, textAlign: 'center' }}>
        <h2>Signing you in…</h2>
        {error && <p style={{ color: '#ef4444' }}>{error}</p>}
        {!error && <p>Please wait a moment.</p>}
      </div>
    </div>
  )
}
